import React, { memo, useCallback, useMemo, useState } from "react";
import PropTypes from 'prop-types';
import { Button, Col, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbsDown, faThumbsUp } from "@fortawesome/free-solid-svg-icons";
import { Remark } from 'react-remark';
import dayjs from "dayjs";
import { ratingOf, RATING_LABELS } from "./conversationStats";
import './RowDetailModal.scss';

// Bootstrap puts every modal on 1055, so a detail opened from inside the
// chart drill-down would only win on DOM order. Pin it above instead.
const STACKED_MODAL_Z = 1065;

const RATING_ICONS = { good: faThumbsUp, bad: faThumbsDown, unrated: null };
const RATING_CLASSES = { good: 'text-success', bad: 'text-danger', unrated: 'text-600' };
const RATING_BG_CLASSES = { good: 'bg-soft-success', bad: 'bg-soft-danger', unrated: 'bg-100' };

// Row click doubles as the way to select and copy question text, so don't
// hijack a click the user made to finish a selection.
const isSelecting = () => !!window.getSelection()?.toString();

export const useRowDetail = () => {
    const [detail, setDetail] = useState(null);

    // AdvanceTable calls handleClickRow(event, row, ...).
    const openDetail = useCallback((_event, row) => {
        if (isSelecting()) return;
        setDetail(row?.original ?? null);
    }, []);

    const closeDetail = useCallback(() => setDetail(null), []);

    return { detail, openDetail, closeDetail };
};

const Field = ({ label, children, xs = 6, m = 3 }) => (
    <Col xs={xs} md={m} className="mb-3">
        <h5 className="text-700 mb-1">{label}</h5>
        <div className="text-800">{children}</div>
    </Col>
);

Field.propTypes = {
    label: PropTypes.string,
    children: PropTypes.node,
    xs: PropTypes.number,
    m: PropTypes.number,
};

const RowDetailModal = ({ detail, onHide }) => {
    const rating = detail ? ratingOf(detail) : 'unrated';

    const created = useMemo(() => {
        const date = detail?.createdDate ? dayjs(detail.createdDate) : null;
        return date?.isValid() ? date.format('DD/MM/YYYY HH:mm:ss') : '—';
    }, [detail]);

    // Remounted per row, so remark re-parses instead of showing the last answer.
    const answer = useMemo(() => {
        const markdown = String(detail?.answer ?? '').trim();
        return markdown ? <Remark>{markdown}</Remark> : <span className="text-600">No answer recorded.</span>;
    }, [detail]);

    return (
        <Modal
            show={!!detail}
            onHide={onHide}
            size="lg"
            centered
            scrollable
            style={{ zIndex: STACKED_MODAL_Z }}
            backdropClassName="elvis-detail-backdrop"
        >
            <Modal.Header closeButton>
                <Modal.Title>Question detail</Modal.Title>
            </Modal.Header>
            {detail && (
                <Modal.Body>
                    <Row className="g-2">
                        <Field label="Asked by">{detail.user || 'Unknown'}</Field>
                        <Field label="Created">{created}</Field>
                        <Field label="Knowledge">
                            {detail.knowledgeName || "No match"}
                        </Field>
                        <Field label="Rating">
                            <span className={RATING_CLASSES[rating]}>
                                {RATING_ICONS[rating] && (
                                    <FontAwesomeIcon icon={RATING_ICONS[rating]} className="me-2" />
                                )}
                                {RATING_LABELS[rating]}
                            </span>
                        </Field>
                    </Row>
                    <Row className="g-2">
                        <Field label="Question">{detail.question || "—"}</Field>
                    </Row>
                    <Row className="g-2">
                        <Field label="Answer" xs={12} m={12}>
                            <div className={`border rounded p-3 fs--1 overflow-auto elvis-answer ${RATING_BG_CLASSES[rating]}`}>
                                {answer}
                            </div>
                        </Field>
                    </Row>
                </Modal.Body>
            )}
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
}

RowDetailModal.propTypes = {
    detail: PropTypes.shape({
        question: PropTypes.string,
        answer: PropTypes.string,
        user: PropTypes.string,
        createdDate: PropTypes.string,
        knowledgeName: PropTypes.string,
        isPositive: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
    }),
    onHide: PropTypes.func
};

export default memo(RowDetailModal);
